import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import {
  Loader2, ArrowLeft, Users, Package, Tags, Radio, ShoppingCart, MessageCircle,
  LifeBuoy, Sparkles, CreditCard, Truck, BarChart3, Bell, Settings, Star,
} from 'lucide-react';

// The 14 sections. Phase-1 wired sections open the seller's OWN existing page
// (scoped to this seller) in a new impersonation tab. The rest are present per
// the approved layout but open next phase (marked disabled).
const SECTIONS = [
  { key: 'buyers',   label: 'Buyers',             icon: Users,         to: '/dashboard/customers', ready: true },
  { key: 'products', label: 'Products',           icon: Package,       to: '/dashboard/products',  ready: true },
  { key: 'categories', label: 'Categories',       icon: Tags,          to: '/dashboard/categories', ready: true },
  { key: 'broadcast', label: 'Catalogue Broadcast', icon: Radio,       to: '/dashboard/broadcast', ready: true },
  { key: 'orders',   label: 'Orders',             icon: ShoppingCart,  to: '/dashboard/orders',    ready: true },
  { key: 'whatsapp', label: 'WhatsApp',           icon: MessageCircle, to: '/dashboard/whatsapp',  ready: true },
  { key: 'support',  label: 'Customer Support',   icon: LifeBuoy,      to: '/dashboard/support',   ready: true },
  { key: 'ai',       label: 'AI Assistant',       icon: Sparkles,      to: '/dashboard/ai',        ready: true },
  { key: 'payments', label: 'Payments',           icon: CreditCard,    to: '/dashboard/payments',  ready: true },
  { key: 'shipping', label: 'Shipping',           icon: Truck,         to: '/dashboard/shipping',  ready: true },
  { key: 'reports',  label: 'Reports & Analysis', icon: BarChart3,     to: '/dashboard/analytics', ready: true },
  { key: 'notifications', label: 'Notifications', icon: Bell,          to: '/dashboard/notifications', ready: true },
  { key: 'settings', label: 'Settings',           icon: Settings,      to: '/dashboard/settings',  ready: true },
  { key: 'reviews',  label: 'Reviews',            icon: Star,          to: '/dashboard/reviews',   ready: false },
];

export default function SellerViewCommandCenter() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/vendors/${id}`);
        setV(data.vendor || data);
      } catch (_e) { setV(null); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const open = (s) => {
    if (!s.ready) return;
    window.open(`/admin/seller-view/${id}?to=${encodeURIComponent(s.to)}`, '_blank', 'noopener');
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-fab-text-secondary"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!v) return <div className="p-8 text-red-600">Seller not found.</div>;

  const name = v.business_name || v.name || 'Seller';
  const status = v.suspended ? 'DEACTIVATED' : 'ACTIVE';

  return (
    <div className="p-6" data-testid="seller-view-command-center">
      <Link to="/dashboard/admin/sellers" className="inline-flex items-center gap-1 text-sm text-fab-primary font-semibold mb-4" data-testid="back-to-sellers">
        <ArrowLeft className="w-4 h-4" /> Back to Sellers
      </Link>

      <div className="bg-white border border-fab-primary/40 rounded-xl p-5 mb-6" data-testid="seller-view-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-xl font-bold text-fab-text" data-testid="sv-business-name">{name}</div>
            <div className="text-sm text-fab-text-secondary mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
              <span>Owner: {v.name || v.owner_name || '—'}</span>
              <span>Email: {v.email || '—'}</span>
              <span>Contact: {v.phone || v.contact || '—'}</span>
              <span>Plan: {v.plan_tier || '—'}</span>
              <span>Last Login: {v.last_login_at ? new Date(v.last_login_at).toLocaleString('en-IN') : '—'}</span>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${v.suspended ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`} data-testid="sv-status">{status}</span>
        </div>
        <div className="mt-4">
          <Link to={`/dashboard/admin/sellers/${id}/matrix`} data-testid="sv-matrix-btn" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded border border-fab-primary bg-fab-primary text-white hover:opacity-90">
            <BarChart3 className="w-3.5 h-3.5" /> Matrix
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => open(s)}
              disabled={!s.ready}
              data-testid={`sv-tile-${s.key}`}
              className={`text-left bg-white border border-fab-primary/40 rounded-xl p-4 transition ${s.ready ? 'hover:border-fab-primary hover:shadow-sm cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            >
              <Icon className="w-6 h-6 text-fab-primary mb-2" />
              <div className="font-semibold text-fab-text text-sm">{s.label}</div>
              <div className="text-[11px] text-fab-text-muted mt-0.5">{s.ready ? 'Open seller page' : 'Coming soon'}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
